import React from "react";
import type {
  RenderShapeNode as RenderShapeNodeModel,
  ShapePrimitiveType,
} from "../../../util/src/layout-compiler/types.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";

type RenderShapeNodeProps = {
  node: RenderShapeNodeModel;
  scale: number;
};

type PolygonShapeType = Extract<
  ShapePrimitiveType,
  | "triangle"
  | "rightTriangle"
  | "trapezoid"
  | "parallelogram"
  | "pentagon"
  | "hexagon"
  | "octagon"
  | "star"
>;

const POLYGON_POINTS: Record<PolygonShapeType, string> = {
  triangle: "50,0 100,100 0,100",
  rightTriangle: "0,0 0,100 100,100",
  trapezoid: "20,0 80,0 100,100 0,100",
  parallelogram: "20,0 100,0 80,100 0,100",
  pentagon: "50,0 100,38 82,100 18,100 0,38",
  hexagon: "25,0 75,0 100,50 75,100 25,100 0,50",
  octagon: "30,0 70,0 100,30 100,70 70,100 30,100 0,70 0,30",
  star: "50,0 62,36 100,36 69,58 81,100 50,74 19,100 31,58 0,36 38,36",
};

const toCssLength = (value: unknown): string | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value)}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
};

const parseBorder = (
  ...borderCandidates: Array<unknown>
): { width: number; color: string } => {
  const raw = borderCandidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim().length > 0,
  ) as string | undefined;

  if (!raw) {
    return { width: 0, color: "transparent" };
  }

  const parts = raw.trim().split(/\s+/);
  const widthToken = parts.find((part) => /px$/i.test(part));
  const parsedWidth = Number.parseFloat(widthToken ?? "");
  const width = Number.isFinite(parsedWidth) ? Math.max(0, parsedWidth) : 1;

  const colorCandidate =
    parts[parts.length - 1] &&
    !/(solid|dashed|dotted|double|none)/i.test(parts[parts.length - 1]!)
      ? parts[parts.length - 1]!
      : "#111111";

  return { width, color: colorCandidate };
};

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) {
    return `${rotationTransform} ${extra}`;
  }

  return rotationTransform ?? extra;
}

function isPolygonShapeType(
  shapeType: ShapePrimitiveType,
): shapeType is PolygonShapeType {
  return shapeType in POLYGON_POINTS;
}

/**
 * Renders a compiled shape primitive (rectangle, ellipse, polygon, line).
 */
export function RenderShapeNode({ node, scale }: RenderShapeNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const resolved = node.style;
  const shapeType = node.shapeType;
  const clipStyle = getRenderNodeClipStyle(node, scale);

  const shellStyle: React.CSSProperties = {
    ...(node.style.position === "relative"
      ? {
          position: "relative",
          flexShrink: 0,
        }
      : {
          position: "absolute",
          left: node.x * scale,
          top: node.y * scale,
        }),
    width: node.width * scale,
    height: node.height * scale,
    zIndex: node.zIndex,
    overflow: "hidden",
    transform: composeTransform(node.rotation, resolved.transform),
    transformOrigin: "center center",
    boxSizing: "border-box",
    margin: resolved.margin as string,
    marginTop: resolved.marginTop as string,
    marginRight: resolved.marginRight as string,
    marginBottom: resolved.marginBottom as string,
    marginLeft: resolved.marginLeft as string,
    padding: resolved.padding as string,
    paddingTop: resolved.paddingTop as string,
    paddingRight: resolved.paddingRight as string,
    paddingBottom: resolved.paddingBottom as string,
    paddingLeft: resolved.paddingLeft as string,
    ...clipStyle,
  };

  const baseStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    backgroundColor: (resolved.backgroundColor as string) ?? "#cccccc",
    opacity: resolved.opacity as number | undefined,
    border: resolved.border as string | undefined,
    ...(resolved.borderTop != null && {
      borderTop: resolved.borderTop as string,
    }),
    ...(resolved.borderRight != null && {
      borderRight: resolved.borderRight as string,
    }),
    ...(resolved.borderBottom != null && {
      borderBottom: resolved.borderBottom as string,
    }),
    ...(resolved.borderLeft != null && {
      borderLeft: resolved.borderLeft as string,
    }),
    borderTopLeftRadius: toCssLength(resolved.borderTopLeftRadius),
    borderTopRightRadius: toCssLength(resolved.borderTopRightRadius),
    borderBottomRightRadius: toCssLength(resolved.borderBottomRightRadius),
    borderBottomLeftRadius: toCssLength(resolved.borderBottomLeftRadius),
    boxSizing: "border-box",
  };

  if (shapeType === "ellipse" || shapeType === "oval") {
    baseStyle.borderRadius = "50%";
  } else if (shapeType === "square" || shapeType === "rectangle") {
    baseStyle.borderRadius = toCssLength(resolved.borderRadius);
  } else if (shapeType === "line") {
    baseStyle.height = "2px";
    baseStyle.alignSelf = "center";
    baseStyle.borderRadius = undefined;
  }

  if (isPolygonShapeType(shapeType)) {
    const border = parseBorder(
      resolved.border,
      resolved.borderTop,
      resolved.borderRight,
      resolved.borderBottom,
      resolved.borderLeft,
    );

    return (
      <div {...runtimeProps} style={shellStyle}>
        <div
          style={{ width: "100%", height: "100%", opacity: baseStyle.opacity }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <polygon
              points={POLYGON_POINTS[shapeType]}
              fill={(resolved.backgroundColor as string) ?? "#cccccc"}
              stroke={border.color}
              strokeWidth={border.width}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div {...runtimeProps} style={shellStyle}>
      <div style={baseStyle} />
    </div>
  );
}
